// App entry. Bootstraps:
//   1. Firebase (auth, firestore, functions, messaging, app check)
//   2. TanStack Query for server state
//   3. React Router
//   4. Service worker registration (deferred until after first paint)
//   5. The update flow (lib/appUpdate.ts, IMP-064): a version downloaded in an
//      earlier session is applied here, before the first render

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { AuthProvider } from './lib/auth';
import { queryClient } from './lib/queryClient';
import { registerServiceWorker } from './lib/registerSW';
import {
  applyWaitingUpdateBeforeRender,
  recoverFromStaleChunk,
  startAppUpdateTracking,
} from './lib/appUpdate';
import './lib/firebase'; // side effect only: initializes Firebase + primes App Check
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Missing #root element in index.html');
}

const root = createRoot(rootEl);

// Session tracking starts before anything renders, so the very first tap
// counts: an update may only apply silently before the user has done anything.
startAppUpdateTracking();

// A lazy chunk that fails to load after a deploy means this page still runs
// the previous build; reload onto the current one (at most once a minute).
window.addEventListener('vite:preloadError', recoverFromStaleChunk);

// Mount immediately (IMP-058) — the app tree is no longer gated on App
// Check's token exchange (see the rationale in lib/firebase.ts: Firestore and
// Functions already await a real token per request internally, so an
// app-level gate only added latency). `index.html` carries a static,
// JS-free splash inside #root for the instant before this script even runs;
// React's first commit replaces it. Everything after that first commit is
// covered by <Suspense fallback={<BrandSplash/>}> in App.tsx (route chunk
// loading) and the auth-loading fallback in ProtectedRoute (claims
// resolution), so there is no gap for a blank screen to reappear.
function render(): void {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/*
            v7 future flags:
              - v7_startTransition wraps state updates in React.startTransition.
              - v7_relativeSplatPath changes relative-route resolution inside
                splat (`*`) routes.
            Opting in now silences the console warnings and makes the eventual
            React Router v7 upgrade a no-op for this app.
          */}
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <App />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

async function boot(): Promise<void> {
  // A new version downloaded during an earlier session is applied now, while
  // the static splash is still on screen: the page reloads onto it before
  // anything could be tapped (IMP-064). In the usual case nothing is waiting
  // and this resolves false at once; the lookup is capped at half a second and
  // a takeover that stalls at three, after which the current version renders.
  if (await applyWaitingUpdateBeforeRender()) return;
  render();

  // Register the service worker after the app has mounted so it never blocks
  // first paint. Failures are logged but non-fatal — the PWA still works.
  registerServiceWorker().catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('SW registration failed:', err);
  });
}

void boot();
